import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMobileMenu } from '../../App';
import { getSubscriptionStatus, SubscriptionType } from '../../services/subscriptionService';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

interface TreeChild {
  to?: string;
  label: string;
  icon: string;
  key?: string;
  type?: 'link' | 'tree';
  children?: TreeChild[];
}

interface TreeLink {
  to: string;
  label: string;
  icon: string;
  type: 'link';
}

interface TreeParent {
  key: string;
  label: string;
  icon: string;
  type: 'tree';
  to?: string;
  children?: TreeChild[];
}

interface TreeHeader {
  label: string;
  type: 'header';
}

type TreeItem = TreeLink | TreeParent | TreeHeader;

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, currentUser, userCredits, userProfile } = useAuth();
  const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();

  // State for managing expanded tree items
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({
    'clothes-ai': false,
    'image-enhancement': false,
    'others': false,
    'ai-workflow': false,
    'video-generation': false,
    'ai-designer': false,
    'product-ai': false,
    'active-wear': false,
  });

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const toggleTreeItem = (key: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Use profile data from backend, fallback to Firebase Auth data
  const userEmail = currentUser?.email || '';
  const displayName = userProfile?.name || currentUser?.displayName || (userEmail ? userEmail.split('@')[0] : 'Guest');
  const [avatarError, setAvatarError] = useState(false);
  const photoURLRaw = currentUser?.photoURL || '';
  const photoURL = photoURLRaw.trim();
  const hasPhoto = photoURL.length > 0;
  const isAuthenticated = !!currentUser;

  // State for subscription data
  const [subscriptionData, setSubscriptionData] = useState<{
    type: string;
    isLoading: boolean;
  }>({
    type: 'free',
    isLoading: true
  });

  // Fetch subscription status when user is authenticated
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!isAuthenticated || !currentUser?.uid) {
        setSubscriptionData({ type: 'free', isLoading: false });
        return;
      }

      try {
        const response = await getSubscriptionStatus(currentUser.uid);
        setSubscriptionData({
          type: response.type,
          isLoading: false
        });
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        setSubscriptionData({
          type: 'free',
          isLoading: false
        });
      }
    };

    fetchSubscriptionStatus();
  }, [isAuthenticated, currentUser?.uid]);

  // Format subscription plan display name
  const getSubscriptionDisplayName = (type: string) => {
    if (!type) return 'Free Plan';

    // Handle new API format like "plus_4months", "pro_yearly", etc.
    const planType = type.split('_')[0]; // Extract part before underscore

    switch (planType.toLowerCase()) {
      case 'pro':
        return 'Pro Plan';
      case 'plus':
        return 'Plus Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      case 'free':
      default:
        return 'Free Plan';
    }
  };

  const subscriptionPlan = isAuthenticated
    ? (subscriptionData.isLoading ? 'Loading...' : getSubscriptionDisplayName(subscriptionData.type))
    : 'Demo Plan';

  // Show upgrade banner only for free and plus users, not for pro or enterprise users
  const showUpgradeBanner = isAuthenticated &&
    !subscriptionData.isLoading &&
    (subscriptionData.type === SubscriptionType.FREE || subscriptionData.type === SubscriptionType.PLUS);

  // Define tree structure for navigation with sections
  const treeStructure: TreeItem[] = [
    { type: 'header', label: 'Core Features' },
    { to: '/', label: 'Dashboard', icon: 'fas fa-home', type: 'link' },
    {
      key: 'clothes-ai',
      label: 'Clothes AI Photoshoot',
      icon: 'fas fa-tshirt',
      type: 'tree',
      children: [
        { to: '/try-on', label: 'Clothes AI Photoshoot', icon: 'fas fa-user-circle' },
        { to: '/ecommerce-photoshoot', label: 'Ecommerce Photoshoot', icon: 'fas fa-images' },
        { to: '/ai-poses', label: 'AI Pose Change', icon: 'fas fa-user-ninja' },
        { to: '/fashion-extractor', label: 'Outfit Extractor (Flatlay)', icon: 'fas fa-cut' },
        { to: '/ghost-mannequin', label: 'Ghost Mannequin', icon: 'fas fa-user-secret' },
        { to: '/wrinkle-removal', label: 'Wrinkle Removal', icon: 'fas fa-wand-magic-sparkles' },
      ]
    },
    {
      key: 'active-wear',
      label: 'Active Wear',
      icon: 'fas fa-running',
      type: 'tree',
      children: [
        { to: '/gym-wear-photoshoot', label: 'Gym Wear Photoshoot', icon: 'fas fa-dumbbell' },
        { to: '/yoga-wear-photoshoot', label: 'Yoga Wear Photoshoot', icon: 'fas fa-spa' },
      ]
    },
    {
      key: 'image-enhancement',
      label: 'Image Enhancement',
      icon: 'fas fa-paint-brush',
      type: 'tree',
      children: [
        { to: '/canvas-editor?tool=background-removal', label: 'Background Removal', icon: 'fas fa-eraser' },
        { to: '/ai-background-change', label: 'AI Background Change', icon: 'fas fa-image' },
        { to: '/canvas-editor?tool=upscaler', label: 'Upscaler', icon: 'fas fa-expand-arrows-alt' },
        { to: '/image-editing', label: 'Image Editing', icon: 'fas fa-edit' },
      ]
    },
    {
      key: 'others',
      label: 'Others',
      icon: 'fas fa-ellipsis-h',
      type: 'tree',
      children: [
        {
          key: 'ai-workflow',
          label: 'AI Workflow',
          icon: 'fas fa-project-diagram',
          type: 'tree',
          children: [
            { to: '/ai-workflow?type=front-back-shoot', label: 'Front & Back Shoot', icon: 'fas fa-sync' },
            { to: '/ai-workflow?type=stitched-cloths', label: 'Stitched Cloths', icon: 'fas fa-tshirt' },
          ]
        },
        {
          key: 'video-generation',
          label: 'Video Generation',
          icon: 'fas fa-video',
          type: 'tree',
          children: [
            { to: '/canvas-editor?tool=video-generation', label: 'Video Generator', icon: 'fas fa-film' },
          ]
        },
        {
          key: 'ai-designer',
          label: 'AI Designer',
          icon: 'fas fa-wand-magic-sparkles',
          type: 'tree',
          children: [
            { to: '/ai-designer', label: 'Design Studio', icon: 'fas fa-drafting-compass' },
          ]
        },
        {
          key: 'product-ai',
          label: 'Product AI Photoshoot',
          icon: 'fas fa-magic',
          type: 'tree',
          children: [
            { to: '/photoshoot', label: 'Product Photoshoot', icon: 'fas fa-box' },
          ]
        },
      ]
    },
    { type: 'header', label: 'Management' },
    { to: '/gallery', label: 'My Gallery', icon: 'fas fa-images', type: 'link' },
    { to: '/credit-history', label: 'Credit History', icon: 'fas fa-receipt', type: 'link' },
    { to: '/subscription', label: 'Subscription', icon: 'fas fa-crown', type: 'link' },
  ];

  // For mobile, hide sidebar when not open
  const sidebarClasses = `
    sidebar bg-white text-text-heading border-r border-accent-taupe
    ${collapsed ? 'w-20' : 'w-64'} 
    flex flex-col fixed h-full transition-all duration-300
    md:translate-x-0
    shadow-xl md:shadow-none
    ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
    z-50
  `;

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleMobileMenu}
        ></div>
      )}

      <div className={sidebarClasses}>
        {/* Brand / Logo */}
        <div className="p-3 md:p-3 flex items-center space-x-2.5 border-b border-accent-sand md:border-none">
          <div className="w-7 h-7 bg-secondary-500 rounded-lg flex items-center justify-center text-white shadow-sm shadow-secondary-100 flex-shrink-0">
            <i className="fas fa-camera-retro text-[11px]"></i>
          </div>
          {!collapsed && <span className="text-[17px] font-bold tracking-tight text-text-heading leading-none">SnapMyDesign</span>}
        </div>

        {/* User Profile - Moved to top for now, will move to bottom if needed */}
        {/* We'll follow the Nexus HTML more closely by putting it at the bottom */}

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-3 overflow-y-auto py-2">
          <div className="space-y-3">
            {treeStructure.map((item, index) => {
              if (item.type === 'link') {
                const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center space-x-2.5 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-all duration-200 ${isActive
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'text-text-description hover:bg-accent-sand hover:text-text-heading group'
                      }`}
                    onClick={() => {
                      if (isMobileMenuOpen) {
                        toggleMobileMenu();
                      }
                    }}
                  >
                    <i className={`${item.icon} w-5 text-center transition-transform ${isActive ? 'text-secondary-600' : 'group-hover:text-secondary-600'}`}></i>
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              } else if (item.type === 'tree') {
                const isExpanded = expandedItems[item.key];
                const hasActiveChild = item.children?.some(child => {
                  if (child.type === 'tree') {
                    return child.children?.some(subChild => {
                      if (!subChild.to) return false;
                      const [subChildPath, subChildQuery] = subChild.to.split('?');
                      const isPathMatch = location.pathname === subChildPath || (subChildPath !== '/' && location.pathname.startsWith(subChildPath));
                      if (subChildQuery && isPathMatch) {
                        return location.search.includes(subChildQuery);
                      }
                      return isPathMatch;
                    });
                  } else {
                    if (!child.to) return false;
                    const [childPath, childQuery] = child.to.split('?');
                    const isPathMatch = location.pathname === childPath || (childPath !== '/' && location.pathname.startsWith(childPath));
                    if (childQuery && isPathMatch) {
                      return location.search.includes(childQuery);
                    }
                    return isPathMatch;
                  }
                });

                return (
                  <div key={item.key} className="space-y-0.5">
                    {/* Tree parent item */}
                    <button
                      onClick={() => {
                        toggleTreeItem(item.key);
                        if (item.to) {
                          navigate(item.to);
                        }
                      }}
                      className={`w-full flex items-center space-x-2.5 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-all duration-200 ${hasActiveChild || isExpanded
                        ? 'text-text-heading bg-accent-sand/50'
                        : 'text-text-description hover:bg-accent-sand hover:text-text-heading group'
                        }`}
                    >
                      <i className={`${item.icon} w-5 text-center transition-colors ${hasActiveChild ? 'text-secondary-600' : 'group-hover:text-secondary-600'}`}></i>
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-[9px] opacity-40`}></i>
                        </>
                      )}
                    </button>

                    {/* Tree children */}
                    {!collapsed && isExpanded && item.children && item.children.length > 0 && (
                      <div className="ml-3.5 pl-3.5 border-l border-accent-taupe space-y-0.5 mt-0.5">
                        {item.children.map(child => {
                          if (child.type === 'tree') {
                            const isSubExpanded = child.key ? expandedItems[child.key] : false;
                            const hasActiveSubChild = child.children?.some(subChild => {
                              if (!subChild.to) return false;
                              const [subChildPath, subChildQuery] = subChild.to.split('?');
                              const isPathMatch = location.pathname === subChildPath || (subChildPath !== '/' && location.pathname.startsWith(subChildPath));
                              if (subChildQuery && isPathMatch) {
                                return location.search.includes(subChildQuery);
                              }
                              return isPathMatch;
                            });

                            return (
                              <div key={child.key || child.label} className="space-y-0.5">
                                <button
                                  onClick={() => {
                                    if (child.key) {
                                      toggleTreeItem(child.key);
                                    }
                                    if (child.to) {
                                      navigate(child.to);
                                    }
                                  }}
                                  className={`w-full flex items-center space-x-2.5 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-all duration-200 ${hasActiveSubChild || isSubExpanded
                                    ? 'text-text-heading bg-accent-sand/30'
                                    : 'text-text-description hover:bg-accent-sand hover:text-text-heading group'
                                    }`}
                                >
                                  <i className={`${child.icon} w-4 text-center text-[11px] transition-colors ${hasActiveSubChild ? 'text-secondary-600' : 'group-hover:text-secondary-600'}`}></i>
                                  <span className="flex-1 text-left">{child.label}</span>
                                  <i className={`fas fa-chevron-${isSubExpanded ? 'down' : 'right'} text-[8px] opacity-40`}></i>
                                </button>

                                {isSubExpanded && child.children && child.children.length > 0 && (
                                  <div className="ml-3.5 pl-3.5 border-l border-accent-taupe/60 space-y-0.5 mt-0.5">
                                    {child.children.map(subChild => {
                                      if (!subChild.to) return null;
                                      const [subChildPath, subChildQuery] = subChild.to.split('?');
                                      const isPathMatch = location.pathname === subChildPath || (subChildPath !== '/' && location.pathname.startsWith(subChildPath));
                                      let isSubActive = isPathMatch;
                                      if (subChildQuery && isPathMatch) {
                                        isSubActive = location.search.includes(subChildQuery);
                                      }

                                      return (
                                        <Link
                                          key={subChild.to}
                                          to={subChild.to}
                                          className={`flex items-center space-x-2.5 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-colors ${isSubActive
                                            ? 'text-secondary-700 bg-secondary-100/50'
                                            : 'text-text-description hover:text-text-heading hover:bg-accent-sand'
                                            }`}
                                          onClick={() => {
                                            if (isMobileMenuOpen) {
                                              toggleMobileMenu();
                                            }
                                          }}
                                        >
                                          <i className={`${subChild.icon} w-4 text-center text-[11px] opacity-60`}></i>
                                          <span>{subChild.label}</span>
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          } else {
                            if (!child.to) return null;
                            const [childPath, childQuery] = child.to.split('?');
                            const isPathMatch = location.pathname === childPath || (childPath !== '/' && location.pathname.startsWith(childPath));
                            let isActive = isPathMatch;
                            if (childQuery && isPathMatch) {
                              isActive = location.search.includes(childQuery);
                            }

                            return (
                              <Link
                                key={child.to}
                                to={child.to}
                                className={`flex items-center space-x-2.5 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-colors ${isActive
                                  ? 'text-secondary-700 bg-secondary-100/50'
                                  : 'text-text-description hover:text-text-heading hover:bg-accent-sand'
                                  }`}
                                onClick={() => {
                                  if (isMobileMenuOpen) {
                                    toggleMobileMenu();
                                  }
                                }}
                              >
                                <i className={`${child.icon} w-4 text-center text-[11px] opacity-60`}></i>
                                <span>{child.label}</span>
                              </Link>
                            );
                          }
                        })}
                      </div>
                    )}
                  </div>
                );
              } else if (item.type === 'header') {
                return !collapsed ? (
                  <div key={item.label} className="pt-1.5 pb-0.5">
                    <p className="px-2 text-[9px] font-bold text-text-description uppercase tracking-widest">{item.label}</p>
                  </div>
                ) : (
                  <div key={item.label} className="border-t border-accent-taupe my-2 mx-3"></div>
                );
              }
              return null;
            })}
          </div>



          {/* Upgrade Banner - show only for free users */}
          {!collapsed && showUpgradeBanner && (
            <div className="px-3 py-2 border-t border-accent-sand mt-1">
              <div className="bg-secondary-100/70 rounded-lg p-2.5">
                <div className="flex items-center space-x-2">
                  <i className="fas fa-rocket text-secondary-600 text-[11px]"></i>
                  <span className="text-[11px] font-bold text-secondary-800">Upgrade to Pro</span>
                </div>
                <p className="text-[9px] text-secondary-700/60 mt-0.5 leading-tight">Unlock advanced features</p>
                <Link
                  to="/upgrade"
                  className="mt-2 w-full bg-secondary-500 hover:bg-secondary-600 text-white text-[10px] font-semibold py-1 px-3 rounded block text-center transition-colors"
                >
                  Upgrade Now
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Credits Display */}
        {isAuthenticated && (
          <div className={`border-t border-accent-taupe py-2 px-3.5 ${collapsed ? 'text-center' : ''} bg-accent-sand/20`}>
            <div className="flex items-center space-x-2">
              <i className="fas fa-coins text-gold text-xs"></i>
              {!collapsed && (
                <div className="flex flex-col">
                  <span className="text-[9px] text-text-description font-bold uppercase tracking-tight leading-none">Available Credits</span>
                  <p className="text-[13px] font-bold text-text-heading mt-0.5 leading-none">{userCredits.toFixed(2)}</p>
                </div>
              )}
              {collapsed && <span className="text-xs text-text-heading font-bold">{userCredits.toFixed(2)}</span>}
            </div>
          </div>
        )}

        {/* User Profile Section - Nexus Style */}
        <div className="p-3 border-t border-accent-taupe bg-accent-sand/50 mt-auto">
          <div className="flex items-center space-x-2.5">
            <div className="relative flex-shrink-0">
              {hasPhoto && !avatarError ? (
                <img
                  src={photoURL}
                  alt="User avatar"
                  className="w-8 h-8 rounded-full object-cover border border-accent-taupe shadow-sm"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-white border border-accent-taupe shadow-sm flex items-center justify-center overflow-hidden">
                  <i className="fas fa-user text-text-description text-xs"></i>
                </div>
              )}
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-success-500 rounded-full border-2 border-white"></span>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-text-heading truncate tracking-tight leading-tight">{displayName}</p>
                <p className="text-[11px] text-text-description truncate mt-0.5 leading-tight">{subscriptionPlan}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="border-t border-accent-taupe">
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-2 text-text-description hover:bg-accent-sand hover:text-text-heading transition-colors"
          >
            <i className="fas fa-sign-out-alt w-5 text-center text-[13px]"></i>
            {!collapsed && <span className="text-[13px] font-semibold">Logout</span>}
          </button>

          {/* Collapse Button - Hide on mobile */}
          <button
            onClick={toggleSidebar}
            className="w-full hidden md:flex items-center space-x-3 px-4 py-2 text-text-description hover:text-text-heading transition-colors"
          >
            <i className={`fas fa-chevron-${collapsed ? 'right' : 'left'} w-5 text-center text-[13px]`}></i>
            {!collapsed && <span className="text-[13px] font-semibold">Collapse</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 